import React from 'react';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { ChangeUserPassword } from '../../store/UserSlice';
import { AppDispatch } from '../../store/store';
import { changePasswordSchema, ChangePasswordFormData } from '../../schemas/auth.schema';
import Input from '../form/Input';

export default function ChangePassword() {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: {
      password: '',
      newPassword: '',
      confirmPassword: '',
    },
    mode: 'onSubmit',
  });

  const onSubmit = async (data: ChangePasswordFormData) => {
    try {
      const result = await dispatch(ChangeUserPassword(data));
      if (ChangeUserPassword.fulfilled.match(result)) {
        toast.success(t('profile.passwordChanged'));
        reset();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || t('profile.passwordChangeFailed'));
    }
  };

  return (
    <div className="px-6 md:px-8 py-2 md:py-4">
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Current Password */}
        <div className="flex flex-col md:flex-row">
          <div className="mb-4 w-full md:w-1/2 pr-0 md:pr-4">
            <Input
              id="password"
              label={t('profile.currentPasswordLabel')}
              type="password"
              placeholder={t('profile.currentPasswordPlaceholder')}
              showPasswordToggle={true}
              error={errors.password?.message}
              {...register('password')}
            />
          </div>
        </div>

        {/* New Password & Confirm Password */}
        <div className="flex flex-col md:flex-row">
          <div className="mb-4 w-full md:w-1/2 pr-0 md:pr-4">
            <Input
              id="newPassword"
              label={t('profile.newPasswordLabel')}
              type="password"
              placeholder={t('profile.newPasswordPlaceholder')}
              showPasswordToggle={true}
              error={errors.newPassword?.message}
              {...register('newPassword')}
            />
          </div>

          <div className="mb-4 w-full md:w-1/2 pr-0 md:pr-4">
            <Input
              id="confirmPassword"
              label={t('profile.confirmPasswordLabel')}
              type="password"
              placeholder={t('profile.confirmPasswordPlaceholder')}
              showPasswordToggle={true}
              error={errors.confirmPassword?.message}
              {...register('confirmPassword')}
            />
          </div>
        </div>

        {/* Submit Button */}
        <div className="pt-2 pb-4">
          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-[#037897] text-white font-semibold float-right w-auto min-w-[160px] px-8 py-3 whitespace-nowrap rounded-md hover:bg-[#025f77] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? t('common.loading') : t('profile.changePasswordBtn')}
          </button>
        </div>
      </form>
    </div>
  );
}
